import { createElement, useState } from "react";
import { Plus, Trash2, ChevronUp, ChevronDown, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImageField } from "@/components/ImageField";
import { VideoField } from "@/components/VideoField";
import { TextField } from "@/pages/beheer/TextField";
import { BLOCK_SCHEMAS, leegItem } from "@/pages/beheer/blockSchema";

const Veld = ({ field, value, onChange, pad }) => {
  const testId = `${pad}-${field.key}`;
  switch (field.type) {
    case "t":
      return <TextField label={field.label} value={value || {}} onChange={onChange} rows={field.rows || 0} />;
    case "image":
      return <ImageField label={field.label} value={value || ""} onChange={onChange} testId={testId} />;
    case "video":
      return <VideoField label={field.label} value={value || ""} onChange={onChange} testId={testId} />;
    case "number":
      return (
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">{field.label}</Label>
          <Input type="number" step={field.step} value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))} data-testid={testId} />
        </div>
      );
    case "select":
      return (
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">{field.label}</Label>
          <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={value ?? field.options[0][0]} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
            {field.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      );
    case "items":
      // createElement doorbreekt de JSX-cyclus Veld → ItemsVeld → Velden → Veld voor de build-plugin
      return createElement(ItemsVeld, { field, value: value || [], onChange, pad: testId });
    default:
      return (
        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">{field.label}</Label>
          <Input value={value || ""} placeholder={field.placeholder} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
        </div>
      );
  }
};

export const Velden = ({ fields, value, onChange, pad }) => (
  <div className="space-y-4">
    {fields.map((f) => (
      <Veld key={f.key} field={f} value={value?.[f.key]} onChange={(v) => onChange({ ...value, [f.key]: v })} pad={pad} />
    ))}
  </div>
);

const ItemsVeld = ({ field, value, onChange, pad }) => {
  const [open, setOpen] = useState(() => new Set());
  const toggle = (i) => setOpen((s) => { const n = new Set(s); n.has(i) ? n.delete(i) : n.add(i); return n; });
  const update = (i, v) => onChange(value.map((x, j) => (j === i ? v : x)));
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= value.length) return; const n = [...value]; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  const samenvatting = (item) => {
    const eerste = field.fields[0];
    const v = item?.[eerste.key];
    return (typeof v === "string" ? v : v?.nl) || `${field.itemLabel} zonder titel`;
  };
  return (
    <div className="space-y-2">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{field.label} ({value.length})</Label>
      {value.map((item, i) => (
        <div key={i} className="rounded-lg border bg-sand-light" data-testid={`${pad}-item-${i}`}>
          <div className="flex items-center gap-1 px-2 py-1.5">
            <button type="button" onClick={() => toggle(i)} className="flex flex-1 items-center gap-2 text-left text-sm font-medium" data-testid={`${pad}-item-${i}-toggle`}>
              <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${open.has(i) ? "rotate-90" : ""}`} />
              <span className="font-mono text-[10px] text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
              <span className="truncate">{samenvatting(item)}</span>
            </button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(i, -1)} aria-label="Omhoog"><ChevronUp className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => move(i, 1)} aria-label="Omlaag"><ChevronDown className="h-3.5 w-3.5" /></Button>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onChange(value.filter((_, j) => j !== i))} aria-label="Verwijderen" data-testid={`${pad}-item-${i}-remove`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </div>
          {open.has(i) && (
            <div className="border-t p-3">
              <Velden fields={field.fields} value={item} onChange={(v) => update(i, v)} pad={`${pad}-${i}`} />
            </div>
          )}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={() => { onChange([...value, leegItem(field.fields)]); setOpen((s) => new Set([...s, value.length])); }} data-testid={`${pad}-add`}>
        <Plus className="mr-1 h-4 w-4" />{field.itemLabel} toevoegen
      </Button>
    </div>
  );
};

export const BlockEditor = ({ block, onChange, onRemove, onUp, onDown }) => {
  const [open, setOpen] = useState(false);
  const schema = BLOCK_SCHEMAS[block.type] || { label: block.type, fields: [] };
  const titel = block.titel?.nl || "";
  return (
    <div className="rounded-xl border bg-card" data-testid="block-editor">
      <div className="flex items-center gap-2 px-3 py-2">
        <button type="button" onClick={() => setOpen((v) => !v)} className="flex flex-1 items-center gap-3 text-left" data-testid="block-toggle">
          <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-90" : ""}`} />
          <span className="rounded-full bg-primary/10 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-primary">{block.type}</span>
          <span className="truncate text-sm font-medium">{titel || schema.label}</span>
        </button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onUp} aria-label="Omhoog" data-testid="block-up"><ChevronUp className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onDown} aria-label="Omlaag" data-testid="block-down"><ChevronDown className="h-4 w-4" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={onRemove} aria-label="Verwijderen" data-testid="block-remove"><Trash2 className="h-4 w-4" /></Button>
      </div>
      {open && (
        <div className="border-t p-4">
          {schema.uitleg && <p className="mb-4 text-xs text-muted-foreground">{schema.uitleg}</p>}
          <Velden fields={schema.fields} value={block} onChange={onChange} pad={`block-${block.type}`} />
        </div>
      )}
    </div>
  );
};
